/** Подписи ячеек таблиц дерева и P&L — значение крупно, под ним мелко доля / штуки / Δ. Чистые функции, без React.
 *
 *  Знаки и цвет — по соглашению `calc.ts`: расходы (`kind: "expense"`) показываются
 *  положительными обычным цветом, их доля выручки — тоже положительная; Δ и Δ п.п.
 *  считаются по показанному числу, рост расхода — красный. Отрицательный расход (компенсация) —
 *  с «−», обычным цветом, с подсказкой «уменьшает расходы». Выручка и маржа — как в витрине,
 *  отрицательная маржа — красным (`negative`).
 *
 *  Дерево — схема «Выручка − Расходы = Маржа» (решение владельца 22.10.2026): колонки «Выручка»,
 *  «Расходы» (`expenses` = удержания площадки + себестоимость) и «Маржа» (`net_margin`). Без
 *  себестоимости (период раньше `cogs_from`) «Расходы» и «Маржа» — «—» с подсказкой, а не удержания
 *  и не маржа до себестоимости. Ключи метрик — прежние (`cost`, `margin`: адрес `tsort`). */
import type { TreeMetrics, TreeRow } from "@/api/ozonAnalytics";
import {
  delta,
  deltaTone,
  REV_EPS,
  share,
  shareDelta,
  shown,
  shownTitle,
  type Delta,
  type Kind,
  type ShareBase,
  type Tone,
} from "./calc";
import { cogsTotal, EXPENSES_LABEL, NET_MARGIN_LABEL } from "./cogs";
import { int, intSigned, pct, pctSigned, pp, rub, rubSigned } from "./format";

/** Себестоимость в дереве: подсказки «—» «Расходов» и «Маржи» у периода и сравнения (без
 *  себестоимости их нет), площадка — в подсказке состава расходов. */
export interface TreeCogs {
  nullTitle: string;
  nullTitleCmp: string;
  mpLabel: string;
}

/** Кусок второй строки; tone null — обычный приглушённый цвет. */
export interface Part {
  text: string;
  tone: Tone | null;
}

export interface CellText {
  /** крупная строка; "" — пусто (сумма ≈ 0) */
  main: string;
  /** цвет крупной строки: negative — доход (выручка, маржа) с минусом, Tone — Δ; расход — без цвета */
  mainTone: Tone | "negative" | null;
  /** вторая строка: куски через « · »; пусто — второй строки нет */
  sub: Part[];
  title: string | null;
}

/** Порог шума Δ п.п. (в долях): 0,05 п.п. */
export const PP_EPS = 0.0005;
const isEmpty = (x: number): boolean => Math.abs(x) < 0.5;
/** Цвет показанного значения: отрицательный доход — красный, расход — всегда обычный. */
const valueTone = (v: number, kind: Kind): CellText["mainTone"] =>
  kind === "income" && v < -0.5 ? "negative" : null;

/** Δ суммы: рубли и Δ % — один тон, по роду суммы. */
function deltaParts(d: Delta, kind: Kind): { abs: Part; rel: Part } {
  const tone = deltaTone(d.abs, 0.5, kind);
  return {
    abs: { text: rubSigned(d.abs), tone },
    rel: { text: pctSigned(d.rel), tone },
  };
}

function ppPart(diff: number | null, kind: Kind): Part | null {
  return diff === null
    ? null
    : { text: pp(diff), tone: deltaTone(diff, PP_EPS, kind) };
}

/** «было: 1 000 ₽ · Δ +200 ₽ · +20,0%» */
function wasLine(cmp: number, d: Delta): string {
  return `было: ${rub(cmp)} · Δ ${rubSigned(d.abs)} · ${pctSigned(d.rel)}`;
}

/** Доля расходов узла (положительная — как показанный расход): от своей выручки, а без неё —
 *  от выручки ближайшего предка. */
export interface CostShare {
  value: number | null;
  ofBase: boolean;
  /** подсказка: от чьей выручки доля (только для доли от предка) */
  title: string | null;
}

export function costShare(
  cost: number,
  m: TreeMetrics,
  base: ShareBase | null,
): CostShare {
  if (m.rev > REV_EPS) return { value: cost / m.rev, ofBase: false, title: null };
  if (base && base.rev > REV_EPS) {
    return {
      value: cost / base.rev,
      ofBase: true,
      title: `Доля — от выручки: ${base.name}`,
    };
  }
  return { value: null, ofBase: true, title: null };
}

/** Маржа % узла (только у узла с выручкой и с маржой). */
export function marginPct(m: TreeMetrics | null): number | null {
  return m && m.rev > REV_EPS && m.net_margin != null ? m.net_margin / m.rev : null;
}

/** Одна метрика дерева в компактном виде: значение (без Δ), Δ отдельно, подсказка. */
interface TreeMetricText {
  value: CellText;
  /** Δ суммы (₽ и %) — null, если сравнения нет или обе суммы ≈ 0 */
  delta: { abs: Part; rel: Part } | null;
  /** Δ п.п. доли (расходы — доля выручки, маржа — маржа %); null — доли не сравнить */
  ppDelta: Part | null;
  /** у метрики есть колонка «Δ п.п.» (у выручки нет) */
  hasPp: boolean;
  /** подсказка ячеек Δ и Δ п.п.: подсказка значения без пометки о самом значении («уменьшает расходы») */
  deltaTitle: string | null;
  /** те же числа, что показаны в ячейках, — для сортировки по столбцу */
  num: MetricNumbers;
  /** Δ не посчитать, потому что одного из чисел нет (без себестоимости): «—» с этой подсказкой */
  deltaDash?: string | null;
}

/** Числа ячеек одной метрики дерева (как показаны): null — в ячейке пусто или «—». */
export interface MetricNumbers {
  /** крупная строка значения: ₽ (у расходов — показанный расход) */
  value: number | null;
  /** мелкая строка значения: шт / доля выручки / маржа % */
  sub: number | null;
  /** Δ ₽ и Δ % (в долях) */
  dAbs: number | null;
  dRel: number | null;
  /** Δ п.п. (в долях) */
  pp: number | null;
}

export interface TreeCellNumbers {
  rev: MetricNumbers;
  /** «Расходы» (`expenses`: удержания + себестоимость, показанный расход) */
  cost: MetricNumbers;
  /** «Маржа» (`net_margin`) */
  margin: MetricNumbers;
}

function joinTitle(lines: (string | null)[]): string | null {
  const out = lines.filter((l): l is string => !!l);
  return out.length ? out.join("\n") : null;
}

type TreeMetricsText = Record<TreeMetricId, TreeMetricText>;

const NO_NUM: MetricNumbers = { value: null, sub: null, dAbs: null, dRel: null, pp: null };
const dashCell = (title: string): CellText => ({ main: "—", mainTone: "neutral", sub: [], title });

/** Метрика без значения (нет себестоимости): «—» с подсказкой у значения и Δ. */
function dashMetric(title: string, hasCmp: boolean): TreeMetricText {
  return {
    value: dashCell(title),
    delta: null,
    ppDelta: null,
    hasPp: true,
    deltaTitle: title,
    num: NO_NUM,
    deltaDash: hasCmp ? title : null,
  };
}

/** «Расходы» узла (`expenses` — удержания + себестоимость, показанные расходом): «X% выр.» (своей
 *  или предка), Δ п.п. доли — только если доли одного вида. Нет себестоимости — «—». */
function expensesMetric(
  row: TreeRow,
  base: ShareBase | null,
  baseCmp: ShareBase | null,
  hasCmp: boolean,
  cogs: TreeCogs,
): TreeMetricText {
  const cur = row.cur;
  const cmp = hasCmp ? row.cmp : null;
  const expOf = (m: TreeMetrics): number | null =>
    m.expenses == null ? null : shown(m.expenses, "expense");
  const v = expOf(cur);
  if (v === null) return dashMetric(cogs.nullTitle, hasCmp);
  const c = cmp ? expOf(cmp) : null;
  const d = hasCmp && c !== null && !(isEmpty(v) && isEmpty(c)) ? delta(v, c) : null;
  const sCur = costShare(v, cur, base);
  const sCmp = cmp && c !== null ? costShare(c, cmp, baseCmp) : null;
  const sDiff =
    sCmp && sCur.ofBase === sCmp.ofBase ? shareDelta(sCur.value, sCmp.value) : null;
  const cogsAll = cogsTotal(cur.cogs_goods, cur.cogs_wh);
  const parts =
    cogsAll === null
      ? null
      : `удержания ${cogs.mpLabel} ${rub(shown(cur.cost, "expense"))} + себестоимость ` +
        `${rub(shown(cogsAll, "expense"))}`;
  // «уменьшает расходы» — про само значение: в подсказке Δ не повторяем
  const detail = joinTitle([
    sCur.title,
    parts,
    d && c !== null ? wasLine(c, d) : null,
    d && sCmp ? `доля выручки: было ${pct(sCmp.value)} · Δ ${pp(sDiff)}` : null,
    hasCmp && c === null ? cogs.nullTitleCmp : null,
  ]);
  return {
    value: {
      main: isEmpty(v) ? "" : rub(v),
      mainTone: valueTone(v, "expense"),
      sub: !isEmpty(v) && sCur.value !== null ? [{ text: `${pct(sCur.value)} выр.`, tone: null }] : [],
      title: joinTitle([shownTitle(v, "expense"), detail]),
    },
    delta: d ? deltaParts(d, "expense") : null,
    ppDelta: d ? ppPart(sDiff, "expense") : null,
    hasPp: true,
    deltaTitle: detail,
    num: {
      value: v,
      sub: !isEmpty(v) ? sCur.value : null,
      dAbs: d?.abs ?? null,
      dRel: d?.rel ?? null,
      pp: d ? sDiff : null,
    },
    deltaDash: hasCmp && c === null ? cogs.nullTitleCmp : null,
  };
}

export const REFERENCE_NO_MARGIN = "Справочно: в P&L не входит — маржи нет";

/** «Маржа» узла (`net_margin` = выручка − расходы): под ней маржа % своей выручки, Δ п.п. — по ней.
 *  Нет себестоимости — «—» (не маржа до себестоимости). */
function marginMetric(row: TreeRow, hasCmp: boolean, cogs: TreeCogs): TreeMetricText {
  // справочная группа и её статьи (WB `spp`): в P&L не входят — маржи нет (бэкенд отдаёт null),
  // в «Расходах» — только своя сумма
  if (row.flags.reference) return dashMetric(REFERENCE_NO_MARGIN, hasCmp);
  const cur = row.cur;
  const cmp = hasCmp ? row.cmp : null;
  const v = cur.net_margin ?? null;
  if (v === null) return dashMetric(cogs.nullTitle, hasCmp);
  const c = cmp ? (cmp.net_margin ?? null) : null;
  const d = hasCmp && c !== null && !(isEmpty(v) && isEmpty(c)) ? delta(v, c) : null;
  const mCur = marginPct(cur);
  const mCmp = cmp && c !== null ? marginPct(cmp) : null;
  const mDiff = shareDelta(mCur, mCmp);
  const title = joinTitle([
    d && c !== null ? wasLine(c, d) : null,
    d && cmp ? `маржа %: было ${pct(mCmp)} · Δ ${pp(mDiff)}` : null,
    hasCmp && c === null ? cogs.nullTitleCmp : null,
  ]);
  return {
    value: {
      main: isEmpty(v) ? "" : rub(v),
      mainTone: valueTone(v, "income"),
      sub: mCur !== null ? [{ text: pct(mCur), tone: null }] : [],
      title,
    },
    delta: d ? deltaParts(d, "income") : null,
    ppDelta: d ? ppPart(mDiff, "income") : null,
    hasPp: true,
    deltaTitle: title,
    num: { value: v, sub: mCur, dAbs: d?.abs ?? null, dRel: d?.rel ?? null, pp: d ? mDiff : null },
    deltaDash: hasCmp && c === null ? cogs.nullTitleCmp : null,
  };
}

function treeMetrics(
  row: TreeRow,
  base: ShareBase | null,
  baseCmp: ShareBase | null,
  hasCmp: boolean,
  cogs: TreeCogs,
): TreeMetricsText {
  const cur = row.cur;
  const cmp = hasCmp ? row.cmp : null;

  const pair = (
    pick: (m: TreeMetrics) => number,
  ): { v: number; d: Delta | null; c: number | null } => {
    const v = pick(cur);
    if (!cmp) return { v, d: null, c: null };
    const c = pick(cmp);
    return { v, c, d: isEmpty(v) && isEmpty(c) ? null : delta(v, c) };
  };

  // выручка: «N шт», Δ ₽/%
  const r = pair((m) => m.rev);
  const q = pair((m) => m.qty);
  const revTitle = joinTitle([
    r.d && r.c !== null ? wasLine(r.c, r.d) : null,
    q.d && q.c !== null
      ? `шт: было ${int(q.c)} · Δ ${intSigned(q.d.abs)}`
      : null,
  ]);
  const rev: TreeMetricText = {
    value: {
      main: isEmpty(r.v) ? "" : rub(r.v),
      mainTone: valueTone(r.v, "income"),
      sub: isEmpty(q.v) ? [] : [{ text: `${int(q.v)} шт`, tone: null }],
      title: revTitle,
    },
    delta: r.d ? deltaParts(r.d, "income") : null,
    ppDelta: null,
    hasPp: false,
    deltaTitle: revTitle,
    num: { value: r.v, sub: q.v, dAbs: r.d?.abs ?? null, dRel: r.d?.rel ?? null, pp: null },
  };
  return {
    rev,
    cost: expensesMetric(row, base, baseCmp, hasCmp, cogs),
    margin: marginMetric(row, hasCmp, cogs),
  };
}

const EMPTY_CELL: CellText = { main: "", mainTone: null, sub: [], title: null };
/** Δ п.п. посчитать нельзя (нет доли в одном из периодов, доли разного вида) — прочерк. */
const NO_PP: CellText = { main: "—", mainTone: "neutral", sub: [], title: null };

/** Измерения статей P&L: ниже них «маржа» повторяет саму статью со знаком. */
const PNL_DIMS = new Set(["grp", "item", "method"]);
export const MARGIN_HIDDEN_TITLE =
  "Маржа считается до разбивки на статьи P&L: ниже статьи она повторяет саму статью";

/** Маржу узла не показываем, если в его пути (предки + он сам) есть статьи P&L. */
export function marginHidden(levelDims: string[]): boolean {
  return levelDims.some((d) => PNL_DIMS.has(d));
}

const MARGIN_OFF: CellText = {
  main: "—",
  mainTone: "neutral",
  sub: [],
  title: MARGIN_HIDDEN_TITLE,
};

/** Ячейки строки дерева — в порядке `treeColumns`: на метрику значение, [Δ п.п.,] Δ
 *  (без сравнения — только значения). Δ — рубли, под ними Δ %; Δ п.п. — отдельной ячейкой
 *  левее Δ (у выручки её нет). Обе суммы ≈ 0 — ячейки Δ и Δ п.п. пустые. */
export function treeCompactCells(
  row: TreeRow,
  base: ShareBase | null,
  baseCmp: ShareBase | null,
  hasCmp: boolean,
  hideMargin: boolean,
  cogs: TreeCogs,
): CellText[] {
  const m = treeMetrics(row, base, baseCmp, hasCmp, cogs);
  return TREE_METRICS.flatMap((id) => {
    const cells = metricCells(m[id], hasCmp);
    // маржа ниже статьи P&L повторяет саму статью — «—»
    return hideMargin && id === "margin" ? cells.map(() => MARGIN_OFF) : cells;
  });
}


/** Числа ячеек строки дерева — ровно те, что показаны (`treeCompactCells`): без сравнения Δ нет,
 *  скрытая маржа (`hideMargin`) — пусто. Для сортировки по столбцу. */
export function treeCellNumbers(
  row: TreeRow,
  base: ShareBase | null,
  baseCmp: ShareBase | null,
  hasCmp: boolean,
  hideMargin: boolean,
  cogs: TreeCogs,
): TreeCellNumbers {
  const m = treeMetrics(row, base, baseCmp, hasCmp, cogs);
  return {
    rev: m.rev.num,
    cost: m.cost.num,
    margin: hideMargin ? NO_NUM : m.margin.num,
  };
}

/** Метрики колонок дерева по порядку: Выручка − Расходы = Маржа. */
const TREE_METRICS: TreeMetricId[] = ["rev", "cost", "margin"];

/** Ячейки одной метрики: значение [, Δ п.п.], Δ (без сравнения — только значение). «—» у
 *  значения (нет себестоимости) — «—» и у Δ. */
function metricCells(x: TreeMetricText, hasCmp: boolean): CellText[] {
  if (!hasCmp) return [x.value];
  const d: CellText = x.delta
    ? {
        main: x.delta.abs.text,
        mainTone: x.delta.abs.tone,
        sub: [x.delta.rel],
        title: x.deltaTitle,
      }
    : x.deltaDash
      ? dashCell(x.deltaDash)
      : EMPTY_CELL;
  if (!x.hasPp) return [x.value, d];
  const ppCell: CellText = !x.delta
    ? d
    : x.ppDelta
      ? {
          main: x.ppDelta.text,
          mainTone: x.ppDelta.tone,
          sub: [],
          title: x.deltaTitle,
        }
      : NO_PP;
  return [x.value, ppCell, d];
}

/** Ячейки строки P&L: «Сейчас», «Было», «Δ п.п.», «Δ» (без сравнения — только «Сейчас»).
 *  cur/cmp — суммы в знаке витрины; kind — род строки (выручка и маржа — доход, удержания —
 *  расход: показываются с обратным знаком, Δ и доля — по показанному). «Δ п.п.» — изменение
 *  доли выручки (у маржи это маржа %, у группы «Выручка» доля всегда 100% — честные 0 п.п.);
 *  нет доли в одном из периодов — «—». «Δ» — рубли, под ними Δ %. */
export function pnlCompactCells(
  cur: number,
  cmp: number | null,
  revCur: number,
  revCmp: number | null,
  hasCmp: boolean,
  kind: Kind,
): CellText[] {
  const v = shown(cur, kind);
  const sCur = share(v, revCur);
  const now: CellText = {
    main: rub(v),
    mainTone: valueTone(v, kind),
    sub: sCur === null ? [] : [{ text: `${pct(sCur)} выр.`, tone: null }],
    title: shownTitle(v, kind),
  };
  if (!hasCmp) return [now];
  const c = cmp === null ? null : shown(cmp, kind);
  const sCmp = c !== null ? share(c, revCmp) : null;
  const was: CellText = {
    main: c === null ? "—" : rub(c),
    mainTone: null,
    sub: sCmp === null ? [] : [{ text: `${pct(sCmp)} выр.`, tone: null }],
    title: c === null ? null : shownTitle(c, kind),
  };
  const d = delta(v, c);
  if (!d) return [now, was, NO_PP, NO_PP];
  const diff = shareDelta(sCur, sCmp);
  const dp = ppPart(diff, kind);
  const ppCell: CellText = dp
    ? {
        main: dp.text,
        mainTone: dp.tone,
        sub: [],
        title: `доля выручки: было ${pct(sCmp)} · сейчас ${pct(sCur)} · Δ ${pp(diff)}`,
      }
    : NO_PP;
  const { abs, rel } = deltaParts(d, kind);
  return [
    now,
    was,
    ppCell,
    {
      main: abs.text,
      mainTone: abs.tone,
      sub: [rel],
      title: `Δ ${abs.text} · Δ % ${rel.text}`,
    },
  ];
}

/** Ячейки строки P&L, суммы которой может не быть (себестоимость, расходы, маржа: null — период
 *  раньше начала себестоимости / у площадки её нет): «—» с подсказкой почему; Δ и Δ п.п. — только
 *  при обоих числах. Иначе — как `pnlCompactCells`. */
export function pnlOptCells(
  cur: number | null,
  cmp: number | null,
  revCur: number,
  revCmp: number | null,
  hasCmp: boolean,
  kind: Kind,
  nullTitle: string,
  nullTitleCmp: string,
): CellText[] {
  if (cur !== null && (!hasCmp || cmp !== null)) {
    return pnlCompactCells(cur, cmp, revCur, revCmp, hasCmp, kind);
  }
  const dash = (title: string): CellText => ({ main: "—", mainTone: "neutral", sub: [], title });
  const known = (v: number, rev: number | null): CellText => {
    const x = shown(v, kind);
    const sh = share(x, rev);
    return {
      main: rub(x),
      mainTone: valueTone(x, kind),
      sub: sh === null ? [] : [{ text: `${pct(sh)} выр.`, tone: null }],
      title: shownTitle(x, kind),
    };
  };
  const now = cur === null ? dash(nullTitle) : known(cur, revCur);
  if (!hasCmp) return [now];
  const was = cmp === null ? dash(nullTitleCmp) : known(cmp, revCmp);
  const why = cur === null ? nullTitle : nullTitleCmp;
  return [now, was, dash(why), dash(why)];
}

/** Ячейки строки справочной группы (WB `spp`, блок «Справочно» под маржой): суммы и Δ — как у
 *  строки P&L, но без долей выручки и «Δ п.п.» (в P&L она не входит). */
export function pnlReferenceCells(
  cur: number,
  cmp: number | null,
  hasCmp: boolean,
): CellText[] {
  // Справочно: знак — как в витрине (СПП −, компенсация +), без «доход/расход» и без цвета Δ —
  // у справочной статьи нет «хорошо/плохо»; долей и Δ % нет (выручки у блока нет).
  const cells: CellText[] = pnlCompactCells(cur, cmp, 0, null, hasCmp, "income").map((c) => ({
    ...c,
    mainTone: null,
    sub: [],
  }));
  if (hasCmp) cells[2] = NO_PP;
  return cells;
}

// ─────────────── ширины колонок ───────────────
// Таблицы — по ширине содержимого (не на весь экран) и с фиксированной раскладкой: колонки не
// растягиваются по широкому экрану и не прыгают при раскрытии строк. Ширины — в ch шрифта таблицы.

/** Числовые колонки: значение («17 242 429 ₽» / «71,2% выр.»), Δ («+13 446 862 ₽» / «−43,8%»)
 *  и Δ п.п. («+12,3 п.п.», с запасом на «+300,0 п.п.» маржи % мелких узлов). */
export const NUM_CH = 16;
export const DELTA_CH = 16;
export const PP_CH = 10;
/** Дерево: колонка узла (подпись переносится). */
export const TREE_LABEL_CH = 40;
/** Дерево: своя, более узкая колонка Δ. */
export const TREE_DELTA_CH = 14;

/** Колонки P&L после статьи: «Сейчас», с сравнением — ещё «Было», «Δ п.п.», «Δ». */
export function pnlColumnsCh(hasCmp: boolean): number[] {
  return hasCmp ? [NUM_CH, NUM_CH, PP_CH, DELTA_CH] : [NUM_CH];
}

/** Колонка дерева после «Узла» — в порядке ячеек `treeCompactCells`. */
export interface TreeColumn {
  /** метрика колонки (по ней — варианты сортировки, `treeSort.ts`) */
  metric: TreeMetricId;
  role: "value" | "pp" | "delta";
  head: string;
  title: string | null;
  ch: number;
}

/** Подсказка шапки «Δ» (сводка и дерево). */
export const DELTA_HEAD_TITLE = "Δ ₽; под ней — Δ %";
export type TreeMetricId = keyof TreeCellNumbers;
const TREE_HEADS: { metric: TreeMetricId; head: string; ppTitle: string | null }[] = [
  { metric: "rev", head: "Выручка", ppTitle: null },
  { metric: "cost", head: EXPENSES_LABEL, ppTitle: "Δ доли расходов в выручке, п.п." },
  { metric: "margin", head: NET_MARGIN_LABEL, ppTitle: "Δ маржи %, п.п." },
];

/** Колонки дерева: Выручка, Расходы (удержания + себестоимость), Маржа; при сравнении — Δ. */
export function treeColumns(hasCmp: boolean): TreeColumn[] {
  return TREE_HEADS.flatMap(({ metric, head, ppTitle }): TreeColumn[] => {
    const value: TreeColumn = { metric, role: "value", head, title: null, ch: NUM_CH };
    if (!hasCmp) return [value];
    const d: TreeColumn = {
      metric,
      role: "delta",
      head: "Δ",
      title: DELTA_HEAD_TITLE,
      ch: TREE_DELTA_CH,
    };
    if (ppTitle === null) return [value, d];
    return [value, { metric, role: "pp", head: "Δ п.п.", title: ppTitle, ch: PP_CH }, d];
  });
}
/** Колонка статей P&L: не уже/не шире; длиннее — обрезается с подсказкой. */
export const PNL_LABEL_CH = { min: 29, max: 59 } as const;

/** Ширина колонки статей P&L — по самой длинной подписи среди ВСЕХ групп и статей, включая
 *  свёрнутые, чтобы раскрытие группы не сдвигало колонки. Запас: у группы — шеврон и счётчик,
 *  у статьи — отступ; у обеих — иконка графика (3 ch). */
export function pnlLabelCh(
  groups: readonly { label: string }[],
  items: readonly { item: string }[],
): number {
  const need = Math.max(
    0,
    ...groups.map((g) => g.label.length + 10),
    ...items.map((r) => r.item.length + 9),
  );
  return Math.min(PNL_LABEL_CH.max, Math.max(PNL_LABEL_CH.min, need));
}
